import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import FormattedMessage from 'components/formatted-message';
import * as endpoints from 'utils/api/endpoints';

import styles from '../auth.module.css';
import { AuthContainer, AuthSubmitButton } from '../components/auth-layout';
import { usePublicPost } from '../hooks/use-public-post';

/** PublicApiController.UnblockConfirmDto: the UUID from the link of the unlock email */
type UnblockConfirmDto = { token: string };

const TOKEN_PARAM = 'token';

const readToken = (search: string) => new URLSearchParams(search).get(TOKEN_PARAM)?.trim() ?? '';

/**
 * /unblock-account?token=…: the page of the unlock email confirms the unlock by POST /public/users/unblock-confirm
 * (POST, so mail scanners that open the link do not unlock the account). An unknown, used or expired token is 404.
 * The token is read once and removed from the address, so it does not stay in the browser history or a copied link
 * (it is single-use: after a reload the page shows the failure).
 */
const UnblockAccount = () => {
    const { pathname, search } = useLocation();
    const history = useHistory();

    const [token] = useState(() => readToken(search));
    const body = useMemo<UnblockConfirmDto | null>(() => (token ? { token } : null), [token]);

    const { status } = usePublicPost({ url: endpoints.unblockConfirm, body });
    const failed = !token || status === 'error';
    const succeeded = !failed && status === 'success';

    useEffect(() => {
        const params = new URLSearchParams(search);
        if (!params.has(TOKEN_PARAM)) {
            return;
        }

        params.delete(TOKEN_PARAM);
        const rest = params.toString();
        history.replace({ pathname, search: rest ? `?${rest}` : '' });
    }, [history, pathname, search]);

    const handleReturnToLogin = useCallback(() => history.push('/login'), [history]);

    return (
        <AuthContainer>
            <div className={styles.messageCard}>
                {!failed && !succeeded && (
                    <h2 className={styles.messageHeader}>
                        <FormattedMessage id="unblockAccount.loading" />
                    </h2>
                )}

                {!!succeeded && (
                    <>
                        <h2 className={styles.messageHeader}>
                            <FormattedMessage id="unblockAccount.success" />
                        </h2>
                        <AuthSubmitButton type="button" onClick={handleReturnToLogin}>
                            <FormattedMessage id="unblockAccount.returnToSignIn" />
                        </AuthSubmitButton>
                    </>
                )}

                {!!failed && (
                    <>
                        <h2 className={styles.failureHeader}>
                            <FormattedMessage id="unblockAccount.failure" />
                        </h2>
                        <p className={styles.failureHint}>
                            <FormattedMessage id="unblockAccount.failureHint" />
                        </p>
                    </>
                )}
            </div>
        </AuthContainer>
    );
};

export default UnblockAccount;
